import { AddressObject } from '../masterdata/Address';

import {
  DATA_EXCHANGE_COMPANY_STATUS,
  DATA_EXCHANGE_COMPANY_STATUS_ORDER,
  DATA_EXCHANGE_COMPANY_TYPE,
} from './constants';
import {
  type DataExchangeCompany,
  type DataExchangeCompanyStatusKeys,
} from './types';

/**
 * Finds the status entry whose `KEY` matches the given status key, or `null`
 * when no entry matches.
 */
function findStatusByKey(statusKey: string) {
  const status = Object.keys(DATA_EXCHANGE_COMPANY_STATUS).find(
    (x) =>
      DATA_EXCHANGE_COMPANY_STATUS[x as DataExchangeCompanyStatusKeys].KEY ===
      statusKey,
  );

  if (!status) {
    return null;
  }

  return DATA_EXCHANGE_COMPANY_STATUS[status as DataExchangeCompanyStatusKeys];
}

export const DataExchangeCompanyObject = {
  /**
   * Creates a data exchange company with the provided data.
   * @param dataExchangeCompany - An object containing the data exchange company data.
   */
  create(dataExchangeCompany: Partial<DataExchangeCompany> = {}) {
    return {
      address: AddressObject.create(dataExchangeCompany?.address),
      blacklisted: dataExchangeCompany?.blacklisted ?? false,
      comment: dataExchangeCompany?.statusComment ?? '',
      companyAccountId: dataExchangeCompany?.companyAccountId ?? '',
      currentIssues: dataExchangeCompany?.currentIssues ?? '',
      furtherInformation: dataExchangeCompany?.furtherInformation ?? '',
      id: dataExchangeCompany?.companyId ?? '',
      internalComment: dataExchangeCompany?.internalStatusComment ?? '',
      internalStatusDeliveryNote: dataExchangeCompany?.internalStatusDln ?? '',
      internalStatusInvoice: dataExchangeCompany?.internalStatusInvoice ?? '',
      isReceiver: dataExchangeCompany?.isReceiver ?? '',
      isSender: dataExchangeCompany?.isSender ?? '',
      knownIssues: dataExchangeCompany?.knownIssues ?? '',
      name: dataExchangeCompany?.name ?? '',
      rank: dataExchangeCompany?.rank ?? '',
      statusDeliveryNote: dataExchangeCompany?.statusDln ?? '',
      statusInvoice: dataExchangeCompany?.statusInvoice ?? '',
    };
  },
  /**
   * Gets a highest status from the data exchange company status.
   * @param statusKey - An string of the data exchange company data.
   */
  getHighestStatus(statusKeys: string) {
    let highestStatusKey = null;

    for (const [_, value] of Object.entries(this.STATUS)) {
      if (highestStatusKey) {
        continue;
      }

      if (statusKeys.includes(value.KEY)) {
        highestStatusKey = value.KEY;
      }
    }

    return highestStatusKey;
  },
  /**
   * Gets a status abbreviated label from the data exchange company status.
   * @param statusKey - An string of the data exchange company data.
   */
  getStatusAbbreviatedLabel(statusKey: string) {
    return findStatusByKey(statusKey)?.ABBREVIATED_LABEL ?? null;
  },
  /**
   * Gets a status description from the data exchange company status.
   * @param statusKey - An string of the data exchange company data.
   */
  getStatusDescription(statusKey: string) {
    return findStatusByKey(statusKey)?.DESCRIPTION ?? null;
  },
  /**
   * Gets a status label from the data exchange company status.
   * @param statusKey - An string of the data exchange company data.
   */
  getStatusLabel(statusKey: string) {
    return findStatusByKey(statusKey)?.LABEL ?? null;
  },
  STATUS: DATA_EXCHANGE_COMPANY_STATUS,
  STATUS_ORDER: DATA_EXCHANGE_COMPANY_STATUS_ORDER,
  TYPE: DATA_EXCHANGE_COMPANY_TYPE,
};
